<script>
  import { onMount, onDestroy } from "svelte";
  import { progress, save, saveNow, exportJson, importJson, resetAll } from "../lib/state.svelte.js";
  import { HUES, PATTERNS, EYES, ACCENTS, organismInner, defaultOrganism, FORM_NAMES } from "../../data/organism.js";
  import { eraForEvo } from "../../data/journeyEngine.js";
  import { getAuthState, installBridge, signIn, signUp, signOut } from "../lib/online.js";
  import { playSound } from "../lib/audio.js";
  import { showToast } from "../lib/effects.js";
  import { install, openInstall } from "../lib/install.svelte.js";
  import CampaignBanner from "./CampaignBanner.svelte";

  const here = $derived(eraForEvo(progress.evo));
  const organism = $derived(progress.organism || defaultOrganism());

  let tick = $state(0);
  let unsub = null;
  onMount(() => {
    unsub = installBridge(() => tick++);
    if (!progress.organism) progress.organism = defaultOrganism();
  });
  onDestroy(() => unsub && unsub());

  const auth = $derived.by(() => {
    tick;
    return getAuthState();
  });

  let email = $state("");
  let password = $state("");
  let mode = $state("signin"); // "signin" | "signup"
  let authBusy = $state(false);
  let authError = $state("");

  let fileInput = $state(null);

  function setOrg(key, value) {
    progress.organism = { ...organism, [key]: value };
    progress.profileUpdatedAt = Date.now(); // 端末をまたいで新しい方を優先させる
    save();
    playSound("click");
  }

  function onNickname(e) {
    progress.nickname = e.currentTarget.value.slice(0, 20);
    progress.profileUpdatedAt = Date.now();
    save();
  }

  const BIO_MAX = 160;
  function onBio(e) {
    progress.bio = e.currentTarget.value.slice(0, BIO_MAX);
    progress.profileUpdatedAt = Date.now();
    save();
  }

  async function submitAuth() {
    authError = "";
    authBusy = true;
    const res =
      mode === "signup"
        ? await signUp(progress.nickname || "ななしの生物", email, password)
        : await signIn(email, password);
    authBusy = false;
    if (res.ok) {
      email = "";
      password = "";
      showToast("✅ ログインしました");
    } else {
      authError = res.error || "うまくいきませんでした。";
    }
  }

  function doExport() {
    saveNow();
    exportJson();
    showToast("⬇ バックアップを保存しました");
  }

  function onFile(e) {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const res = importJson(String(ev.target.result));
      if (res.ok) showToast("⬆ 進捗を復元しました");
      else alert(res.error);
      if (fileInput) fileInput.value = "";
    };
    reader.readAsText(file);
  }

  function doReset() {
    if (!confirm("すべての進捗（解答記録・旅・バッジ）を消します。取り消せません。よろしいですか？")) return;
    if (!confirm("本当に消してよろしいですか？ 先にバックアップを保存することをおすすめします。")) return;
    resetAll();
    showToast("進捗をリセットしました");
  }
</script>

<section>
  <div class="card">
    <h2>🧫 あなたの生き物</h2>
    <p class="muted tiny" style="margin-top:0">
      旅の進み具合に応じて姿が変わります。いまは <b>{FORM_NAMES[here.era.form]}</b>（{here.era.em}
      {here.era.name}）。
    </p>

    <div class="two-col" style="margin-top:14px">
      <div class="organism-preview">
        <svg viewBox="0 0 100 100" aria-label="あなたの生き物">
          {@html organismInner(here.era.form, organism)}
        </svg>
      </div>

      <div>
        <div class="field">
          <label for="nick">ニックネーム（ランキング表示名）</label>
          <input
            id="nick"
            type="text"
            value={progress.nickname}
            oninput={onNickname}
            placeholder="ななしの生物"
            maxlength="20"
          />
        </div>

        <div class="field">
          <label for="bio">自己紹介（ランキングであなたをタップすると表示されます）</label>
          <textarea
            id="bio"
            value={progress.bio}
            oninput={onBio}
            placeholder="好きな分野、目標、ひとことなど。&#10;例：医学部を目指して勉強中。免疫と代謝が好きです。"
            maxlength={BIO_MAX}
            rows="3"
          ></textarea>
          <div class="tiny char-count">{(progress.bio || "").length} / {BIO_MAX}</div>
        </div>

        <div class="field">
          <span class="tiny" style="display:block; margin-bottom:5px">体色</span>
          <div class="swatches">
            {#each HUES as h (h.id)}
              <button
                class="swatch"
                class:sel={organism.hue === h.id}
                style="background:hsl({h.hue} 62% 58%)"
                title={h.name}
                aria-label={h.name}
                onclick={() => setOrg("hue", h.id)}
              ></button>
            {/each}
          </div>
        </div>

        <div class="field">
          <span class="tiny" style="display:block; margin-bottom:5px">模様</span>
          <div class="chip-row">
            {#each PATTERNS as p (p.id)}
              <button class="chip" class:sel={organism.pattern === p.id} onclick={() => setOrg("pattern", p.id)}>
                {p.name}
              </button>
            {/each}
          </div>
        </div>

        <div class="field">
          <span class="tiny" style="display:block; margin-bottom:5px">目</span>
          <div class="chip-row">
            {#each EYES as e (e.id)}
              <button class="chip" class:sel={organism.eyes === e.id} onclick={() => setOrg("eyes", e.id)}>
                {e.name}
              </button>
            {/each}
          </div>
        </div>

        <div class="field">
          <span class="tiny" style="display:block; margin-bottom:5px">特徴</span>
          <div class="chip-row">
            {#each ACCENTS as a (a.id)}
              <button class="chip" class:sel={organism.accent === a.id} onclick={() => setOrg("accent", a.id)}>
                {a.name}
              </button>
            {/each}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <h3>🏆 オンラインランキング</h3>
    {#if !auth.syncEnabled}
      <p class="muted tiny">
        設定されていません。リポジトリ直下の <code>firebase-config.js</code> に自分の Firebase
        プロジェクトの設定値を入れ、Web に公開すると使えるようになります（手順は README）。
        設定しなくても学習機能はすべて動きます。
      </p>
    {:else if auth.user}
      <p class="muted tiny">
        <b>{auth.user.email}</b> でログイン中。進捗は自動でクラウドに同期されます。
      </p>
      <button class="btn ghost" onclick={signOut}>ログアウト</button>
    {:else}
      <CampaignBanner />
      <ul class="perks">
        <li>🎯 <b>間違えた問題が自動で記録され</b>、あとでまとめて解き直せます（正解すると外れます）</li>
        <li>🔖 気になった問題に印を付けて「あとで解き直す」に残せます</li>
        <li>☁️ 進捗・記録が端末をまたいで同期されます</li>
        <li>🏆 ランキングに参加できます</li>
      </ul>
      <div class="chip-row" style="margin-bottom:12px">
        <button class="chip" class:sel={mode === "signin"} onclick={() => (mode = "signin")}>ログイン</button>
        <button class="chip" class:sel={mode === "signup"} onclick={() => (mode = "signup")}>新規登録</button>
      </div>
      <div class="field">
        <label for="em">メールアドレス</label>
        <input id="em" type="email" bind:value={email} autocomplete="email" />
      </div>
      <div class="field">
        <label for="pw">パスワード（6文字以上）</label>
        <input
          id="pw"
          type="password"
          bind:value={password}
          autocomplete={mode === "signup" ? "new-password" : "current-password"}
        />
      </div>
      {#if authError}
        <div class="result ng" style="animation:none">{authError}</div>
      {/if}
      <button class="btn submit" onclick={submitAuth} disabled={authBusy || !email || !password}>
        {authBusy ? "処理中…" : mode === "signup" ? "登録する" : "ログイン"}
      </button>
    {/if}
  </div>

  <div class="card">
    <h3>💾 進捗のバックアップ</h3>
    <p class="muted tiny" style="margin-top:0">
      進捗はこの端末のブラウザ（localStorage）に保存されます。別の端末に移すときは JSON
      で書き出して読み込んでください。
    </p>
    <div class="btn-row">
      <button class="btn run" onclick={doExport}>⬇ バックアップを保存</button>
      <button class="btn run" onclick={() => fileInput?.click()}>⬆ バックアップから復元</button>
      <input
        bind:this={fileInput}
        type="file"
        accept="application/json,.json"
        style="display:none"
        onchange={onFile}
      />
    </div>
  </div>

  <div class="card">
    <h3>⚙️ その他</h3>
    <div class="btn-row">
      <button
        class="btn run"
        onclick={() => {
          progress.soundOn = !progress.soundOn;
          saveNow();
        }}
      >
        {progress.soundOn ? "🔊 効果音：オン" : "🔇 効果音：オフ"}
      </button>
      {#if !install.standalone}
        <button class="btn run" onclick={openInstall}>📲 ホーム画面に追加</button>
      {/if}
      <button class="btn danger" onclick={doReset}>進捗をリセット</button>
    </div>
  </div>
</section>
